import { useTranslation } from 'react-i18next'
import { Button, Grid, Typography } from '@mui/material'
import LocalHeader from '../../components/LocalHeader'
import { useBlocker, useLocation, useNavigate } from 'react-router'
import {TextField} from '@mui/material'
import { textLengthErrorMsg } from '../../utils/functions'
import { use, useActionState, useEffect, useState } from 'react'
import UploadIcon from './UploadIcon'
import { useMe, useUpdateMe } from '../../hooks/useMe'
import { OpenAPIResponseData, UserAPI } from '../../api/api-switcher'
import TwitterBlack from '../../components/UserDetail/twitter_black.svg?react'
import DiscordBlack from '../HelpAndFeedback/discord-mark-black.svg?react'
import { SeamlessSnackbarContext } from '../../contexts/SeamlessSnackbarContext'
import ConfirmLeavePageDialog from '../../components/ConfirmLeavePageDialog'

type Me = OpenAPIResponseData<ReturnType<typeof UserAPI.getMe>>
type FormMe = Pick<Me, 'metamory_id' | 'user_name' | 'user_icon' | 'description' | 'twitter_id' | 'discord_id'>

type Props = {is_init?: boolean}
const EditProfile: React.FC<Props> = ({is_init}) => {
  const {t} = useTranslation()
  const me = useMe()
  const updateMe = useUpdateMe()
  const location = useLocation()
  const navigate = useNavigate()
  const seamlessSnackbar = use(SeamlessSnackbarContext)
  const [form_me, setFormMe] = useState<FormMe>({
    metamory_id: me?.metamory_id || '',
    user_name: me?.user_name || '',
    description: me?.description || '',
    user_icon: me?.user_icon || '',
    twitter_id: me?.twitter_id,
    discord_id: me?.discord_id,
  })
  const name_length_error = textLengthErrorMsg(t, form_me.user_name, 1, 50, 200)
  const id_length_error = textLengthErrorMsg(t, form_me.metamory_id, 3, 16, 200)
  const id_regexp = /^[a-z0-9_]*$/i
  const id_char_error = id_regexp.test(form_me.metamory_id) ? '' : t('profile.user_id_char_error')
  const description_error = textLengthErrorMsg(t, form_me.description, 1, 150, 600)
  const twitter_id_error = textLengthErrorMsg(t, form_me.twitter_id || '', 0, 50, 50)
  const discord_id_error = textLengthErrorMsg(t, form_me.discord_id || '', 0, 50, 50)
  const [, updateMeAction, is_pending] = useActionState<void, FormData>(async (_, form_data)=> {
    const data = {
      metamory_id: form_data.get('metamory_id') as string,
      user_name: form_data.get('user_name') as string,
      description: form_data.get('description') as string,
      twitter_id: form_data.get('twitter_id') as string,
      discord_id: form_data.get('discord_id') as string,
    }
    const is_update = Boolean(me?.user_name)
    const is_change_icon = form_me.user_icon !== me?.user_icon
    const icon_file = is_change_icon ?
        await fetch(form_me.user_icon)
          .then(r=> r.blob())
          .then(b=> new File([b], 'icon.png', {type: 'image/png'}))
      :
        undefined
    const is_success = await updateMe(data, icon_file)
    if (is_success) {
      seamlessSnackbar(t('profile.edit_profile_success'))
      if (me) {
        if (!is_update) {
          navigate('/', {replace: true, state: {unblock: true}})
        }
        else navigate(location.state?.from || '/user', {replace: true, state: {unblock: true}})
      }
    }
  }, undefined)
  const is_changed = Object.keys(form_me).some(key=> form_me[key as keyof FormMe] !== me?.[key as keyof Me])
  const blocker = useBlocker(({nextLocation})=> is_changed && !nextLocation.state?.unblock)
  const [id_exist, setIdExist] = useState<'exist' | 'ok' | 'load'>('ok')
  useEffect(()=> {
    if (form_me.metamory_id.length < 3) return
    if (form_me.metamory_id === me?.metamory_id) {
      setIdExist('ok')
      return
    }
    const con = new AbortController()
    setIdExist('load')
    const timeout = setTimeout(()=> {
      UserAPI.getUserIdFromMetamoryId(form_me.metamory_id, con.signal).then(({data, response})=> {
        if (response.status === 404) setIdExist('ok')
        if (!data) seamlessSnackbar(t('error.fetch'))
        else if (data.user_id === me?.user_id) setIdExist('ok')
        else setIdExist('exist')
      })
    }, 400)
    return ()=> {
      clearTimeout(timeout)
      con.abort()
    }
  }, [form_me.metamory_id])
  const id_exist_error = id_exist === 'exist' ? t('profile.exist_metamory_id') : ''

  return <>
    <LocalHeader
      title={t('profile.edit_profile')}
      back={is_init ? '' : '/user'}
    >
      <Button
        form='create-form'
        type='submit'
        variant='contained'
        color='primary'
        sx={{mr: 2}}
        size='small'
        disabled={!is_changed || Boolean(
          name_length_error || id_length_error || id_char_error ||
          description_error ||
          twitter_id_error || discord_id_error || is_pending ||
          id_exist !== 'ok'
        )}
        startIcon={<span className='material-icons-outlined'>check_circle</span>}
      >
        {t('general.complate')}
      </Button>
    </LocalHeader>

    <main style={{padding: '0 12px', flexGrow: 1, overflow: 'auto'}}>
      <form
        id='create-form'
        style={{ padding: '40px 4px 8px' }}
        action={updateMeAction}
      >
        <UploadIcon
          icon={form_me.user_icon}
          onChange={url=> {
            const icon  = form_me.user_icon
            setFormMe({...form_me, user_icon: url})
            if (icon.startsWith('blob:')) URL.revokeObjectURL(icon)
          }}
        />
        <TextField
          name='metamory_id'
          error={Boolean(id_length_error || id_char_error || id_exist_error)}
          helperText={id_length_error || id_char_error || id_exist_error}
          sx={{mt: 2}}
          required
          slot='htmlInput'
          label={t('profile.user_id')}
          variant='standard'
          fullWidth
          value={form_me.metamory_id}
          inputMode='url'
          slotProps={{htmlInput: {inputMode: 'email'}}}
          onChange={e=> {
            setFormMe({...form_me, metamory_id: e.target.value})
          }}
        />
        <TextField
          name='user_name'
          error={Boolean(name_length_error)}
          helperText={name_length_error}
          sx={{mt: 2}}
          required
          slot='htmlInput'
          label={t('profile.user_name')}
          variant='standard'
          fullWidth
          value={form_me.user_name}
          onChange={e=> {
            const is_space = /\s/.test(e.target.value)
            if (!is_space) setFormMe({...form_me, user_name: e.target.value})
          }}
        />
        <TextField
          name='description'
          error={Boolean(description_error)}
          helperText={description_error}
          sx={{mt: 2}}
          slot='htmlInput'
          label={t('profile.user_description')}
          variant='outlined'
          fullWidth
          required
          multiline
          rows={10}
          value={form_me.description}
          onChange={e=> { setFormMe({...form_me, description: e.target.value}) }}
        />

        <Typography color='gray' sx={{mt: 3}} fontSize={14}>
          {t('profile.collaboration_other_service')}
        </Typography>
        <Grid container spacing={1} mt={2}>
          <Grid sx={{width: 95}}>
            <div style={{height: '100%', width: '100%', alignContent: 'center', textAlign: 'center'}}>
              <a href='https://x.com' target='_blank' rel='noreferrer' style={{color: 'inherit'}}>
                <TwitterBlack style={{height: '24px', width: '50px'}}/>
              </a>
            </div>
          </Grid>
          <Grid size='grow'>
            <TextField
              name='twitter_id'
              label='X(Twitter) ID'
              error={Boolean(twitter_id_error)}
              helperText={twitter_id_error || (form_me.twitter_id ? '' : t('profile.twitter_input_hint'))}
              slot='htmlInput'
              variant='standard'
              fullWidth
              value={form_me.twitter_id}
              onChange={e=> {
                const twitter_id = e.target.value.replace(/^https:\/\/twitter.com\//, '')
                                              .replace(/^https:\/\/x.com\//, '')
                                              .replace(/[/@]/g, '') // リンクとしてヤバそうなの除去
                                              .replace(/\?.*$/, '') // ?以降除去
                setFormMe({...form_me, twitter_id})
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} mt={2}>
          <Grid sx={{width: 95}}>
            <div style={{height: '100%', width: '100%', alignContent: 'center', textAlign: 'center'}}>
              <a href='https://discord.com/channels/@me' target='_blank' rel='noreferrer' style={{color: 'inherit'}}>
                <DiscordBlack style={{height: '32px', width: '32px', marginTop: '12px'}}/>
              </a>
            </div>
          </Grid>
          <Grid size='grow'>
            <TextField
              name='discord_id'
              label='Discord ID'
              error={Boolean(discord_id_error)}
              helperText={discord_id_error}
              slot='htmlInput'
              variant='standard'
              fullWidth
              value={form_me.discord_id}
              onChange={e=> {
                const discord_id = e.target.value.replace(/[/@]/g, '') // リンクとしてヤバそうなの除去
                                                 .replace(/\?.*$/, '') // ?以降除去
                setFormMe({...form_me, discord_id})
              }}
            />
          </Grid>
        </Grid>
      </form>
    </main>
    <ConfirmLeavePageDialog blocker={blocker}/>
  </>
}
export default EditProfile